// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2025-09-21 19:16:35 +0200 (Sun, 21 Sep 2025) $
//  Author    : Tobias Schwarzkopf
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 156393 $
//  Purpose   : wiki parts
// =============================================================================
// ---Third-party imports
import React from 'react'
import {Menu, Grid, Segment, Loader, Table, Button, Icon} from 'semantic-ui-react'

// ---Framework imports
import {dict, mapItems, isArray} from 'ldash'
import log from 'uix/logging'
import {Sel, Actions} from 'uix/registries'
import {useMemo, useState, usePersistent, useApi, useActions, usePartContext} from 'uix/hooks'
import {Link, ConfirmButton} from 'uix/components'
import {DomainLabel} from 'uix/domain/label'
import {formatDate} from 'uix/utils'

// ---Local imports
import {useWikiTemplates} from './hooks'

// ---Globals
export const CursorTagInfo = ({mode, token}) => {
  let wikiTemplates = useWikiTemplates()
  let byTag = useMemo(
    () =>
      dict(
        mapItems(
          wikiTemplates,
          (name, rule) => [rule.tagMatcher, {...rule, name}],
          // only rules created by GenericTagHandler
          (name, rule) => rule.tagMatcher
        )
      ),
    [wikiTemplates]
  )

  let toktype = token?.type
  let tagName = null
  let attrName = null
  if (toktype == 'tag') {
    tagName = token.string
  } else if (toktype == 'attribute') {
    tagName = token.state.tagName
    attrName = token.string
  }

  if (tagName) {
    let rule = byTag[tagName]

    let fmt_attr = ''

    if (attrName) {
      let values = '...'
      if (rule) {
        let compl = rule.completion && rule.completion()
        if (compl) {
          let vs = compl[tagName]?.attrs[attrName]
          values = isArray(vs) ? vs.slice(0, 3).join(',') : '?'
        }
      }
      fmt_attr = ` ${attrName}=${values}`
    }
    return (
      <>
        <div>{`<${tagName}${fmt_attr}>`}</div>
        {rule?.docs && (
          <div>
            <i>{rule.docs}</i>
          </div>
        )}
      </>
    )
  }

  return (
    <>
      <div>
        <tt>{mode.name}</tt>
      </div>
      <div>{token?.type || null}</div>
    </>
  )
}

export const ChainInfo = ({identifier, changedTs}) => {
  let {data} = useApi('getWikiChain', {identifier}, {autoreset: false, deps: changedTs})
  let deleteTreeRecords = useActions(Actions.deleteTreeRecords)
  let {reload} = usePartContext()

  if (!data) {
    return <Loader inline active size="mini" />
  }

  let build = (source, item) => {
    if (!item) {
      return
    }
    let {fspath} = item
    let label = fspath && fspath.startsWith('https://help.vls.io') ? fspath.slice(19) : fspath

    let buttons = null
    if (item.otid) {
      label = (
        <Link dname={source} classname="xwiki" tabname="edit">
          {item.caption || item.otid}
        </Link>
      )

      buttons = (
        <ConfirmButton
          compact
          icon="trash"
          onClick={() =>
            deleteTreeRecords({otid: item.otid, erase: true, onDeleted: () => reload()})
          }
          confirm={`Delete wiki record ${item.otid}`}
        />
      )
    }
    let formatSource = source => ({_hub_: 'help.vls.io', _core_: 'core'}[source] || source)
    return (
      <Table.Row>
        <Table.Cell>
          {item.otid ? <DomainLabel shortform dname={source} /> : formatSource(source)}
        </Table.Cell>
        <Table.Cell>{label}</Table.Cell>
        <Table.Cell>{item.mimetype}</Table.Cell>
        <Table.Cell>{formatDate(item.ts)}</Table.Cell>
        <Table.Cell>{buttons}</Table.Cell>
      </Table.Row>
    )
  }
  let rows = [...data.dnames, '_core_', '_hub_']
    .filter(k => data.wikiChain[k]?.db || data.wikiChain[k]?.fs)
    .map(k => {
      let items = data.wikiChain[k]
      return (
        <React.Fragment key={k}>
          {build(k, items.db)}
          {build(k, items.fs)}
        </React.Fragment>
      )
    })
  if (rows.length == 0) {
    return null
  }

  return (
    <Table compact singleLine>
      <Table.Body>{rows}</Table.Body>
    </Table>
  )
}

export const WikiInfoPane = ({identifier, changedTs, cmToken, cmMode}) => {
  let [active, setActive] = usePersistent('activeWikiInfoPane', 'taginfo')

  let content = null
  //~ return <Tab menu={{fluid: true, vertical: true, tabular: 'right'}} panes={panes} />
  if (active == 'chain' && identifier) {
    content = <ChainInfo changedTs={changedTs} identifier={identifier} />
  } else {
    content = <CursorTagInfo mode={cmMode} token={cmToken} />
  }

  return (
    <Grid>
      <Grid.Column stretched width={13}>
        {content}
      </Grid.Column>

      <Grid.Column width={3}>
        <Menu vertical fluid borderless>
          <Menu.Item
            name="info"
            active={active === 'taginfo'}
            onClick={() => setActive('taginfo')}
          />
          {identifier && (
            <Menu.Item
              name="chain"
              active={active === 'chain'}
              onClick={() => setActive('chain')}
            />
          )}
        </Menu>
      </Grid.Column>
    </Grid>
  )
}
